import { useCallback, useEffect, useRef, useState } from 'react';
import { API_URL, apiFetch, authHeaders } from '../../store';
import { useFedContent } from '../content';

export const DRAW_TOUR = 'nova-dota2-october-2026';

export default function useDraw(tourId = DRAW_TOUR) {
  const [data, setData] = useState(null);
  const [online, setOnline] = useState(false);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const current = useRef(null);
  const writing = useRef(false);
  const epoch = useRef(0);
  const etag = useRef('');
  const syncTour = useCallback((next) => {
    if (!next) return;
    const state = useFedContent.getState();
    const tour = state.tournaments.find((item) => item.id === tourId);
    const patch = { ...next.tour, drawState: next.draw };
    if (tour && Object.entries(patch).some(([key, value]) => JSON.stringify(tour[key]) !== JSON.stringify(value))) {
      useFedContent.setState({ tournaments: state.tournaments.map((item) => item.id === tourId ? { ...item, ...patch } : item) });
    }
  }, [tourId]);
  const apply = useCallback((next) => {
    current.current = next;
    setData(next);
    syncTour(next);
  }, [syncTour]);

  useEffect(() => {
    let stopped = false, timer, controller;
    etag.current = '';
    const poll = async () => {
      const started = epoch.current;
      controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 8000);
      try {
        if (!writing.current) {
          const response = await fetch(`${API_URL}/draw/${encodeURIComponent(tourId)}`, {
            signal: controller.signal, headers: etag.current ? { 'If-None-Match': etag.current } : {}, cache: 'no-cache',
          });
          if (response.status !== 304 && !response.ok) throw new Error('Не удалось загрузить жеребьёвку');
          const next = response.status === 304 ? null : await response.json();
          if (!stopped && started === epoch.current) {
            if (next) { apply(next); etag.current = response.headers.get('ETag') || ''; }
            // /content has a short server cache and may hydrate after this live snapshot.
            else syncTour(current.current);
            setOnline(true);
          }
        }
      } catch (err) { if (!stopped) setOnline(false); }
      finally {
        clearTimeout(timeout);
        if (!stopped) timer = setTimeout(poll, document.hidden ? 3000 : 900);
      }
    };
    poll();
    return () => { stopped = true; clearTimeout(timer); controller?.abort(); };
  }, [tourId, apply, syncTour]);

  const act = useCallback(async (action, values = {}) => {
    if (writing.current || !current.current) return false;
    writing.current = true;
    epoch.current += 1;
    setBusy(true);
    setError('');
    try {
      const response = await apiFetch(`${API_URL}/draw/${encodeURIComponent(tourId)}`, {
        method: 'PUT', headers: authHeaders(),
        body: JSON.stringify({ action, revision: current.current.revision, ...values }),
      });
      const next = await response.json();
      if (!response.ok) {
        if (next.state) apply(next.state);
        throw new Error(next.error || 'Изменения не сохранены');
      }
      apply(next);
      etag.current = '';
      setOnline(true);
      return true;
    } catch (err) { setError(err.message); return false; }
    finally { epoch.current += 1; writing.current = false; setBusy(false); }
  }, [tourId, apply]);
  return { data, online, busy, error, act };
}
